/**
 * TENANT PROTOTYPE — Read-only clickable mock for multi-location org rollout.
 *
 * SCOPE: 3 screens, no DB writes, no production coupling.
 *   1. Stacked Exec Dashboard (Garden City real-shape numbers + synthesized Syosset)
 *   2. Tenant Admin (Glen's view) → Settings → Locations + "Add Another Location" flow
 *   3. Super-Admin → Tenant Management (where you set max_locations cap)
 *
 * GATED: Super-admin only. Direct URL access via /prototype/tenant-view.
 * Not added to any nav. Safe to delete after sign-off.
 */
import { useState, useMemo, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Building2, MapPin, DollarSign, BedDouble, Users, TrendingUp,
  Plus, CheckCircle2, AlertCircle, Loader2, Lock, Settings as SettingsIcon,
  ArrowUp, ArrowDown, FlaskConical, Eye, ShieldCheck, Crown, Layers,
  ChevronRight, ChevronDown, Heart
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

// ── MOCK DATA ────────────────────────────────────────────────────────────
// Garden City uses plausible real-shape numbers; Syosset = GC * ~0.7 with jitter.
// Range multipliers vs YTD baseline — used to synthesize MTD/QTR/Last Month/Last Year views.
const RANGE_OPTIONS = [
  { value: "ytd", label: "YTD", mul: 1.0 },
  { value: "qtr", label: "Quarter to Date", mul: 0.34 },
  { value: "mtd", label: "Month to Date", mul: 0.11 },
  { value: "last_month", label: "Last Month", mul: 0.13 },
  { value: "last_year", label: "Last Year (Full)", mul: 1.32 },
];

const GC = {
  id: "gc",
  name: "Garden City",
  city: "Garden City, NY",
  gingr_partition_id: 1,
  revenue_ytd: 1_248_500,
  revenue_ytd_ly: 1_152_000, // last year same period
  revenue_mtd: 142_300,
  revenue_change: 8.4,
  revenue_mix: { boarding: 712_000, daycare: 318_000, grooming: 142_500, other: 76_000 },
  occupancy_pct: 78,
  occupancy_pct_ly: 75,
  occupancy_change: 4.1,
  occupancy_detail: { boarding_pct: 78, daycare_avg: 62, peak_day: "Sat Apr 19", peak_pct: 94 },
  daycare_avg: 62,
  leads_total: 245,
  leads_total_ly: 214,
  leads_converted: 138,
  leads_converted_ly: 119,
  leads_qualified: 47,
  conversion_rate: 56.3,
  conversion_rate_ly: 55.6,
  dogs_today: 84,
  dogs_today_ly: 79,
  going_home_today: 22,
  status: "active",
  health_score: 95,
};
const SYOSSET = {
  id: "syosset",
  name: "Syosset",
  city: "Syosset, NY",
  gingr_partition_id: 2,
  revenue_ytd: Math.round(GC.revenue_ytd * 0.71),
  revenue_ytd_ly: Math.round(GC.revenue_ytd * 0.71 * 0.91), // up YoY 10%
  revenue_mtd: Math.round(GC.revenue_mtd * 0.68),
  revenue_change: 12.6,
  revenue_mix: {
    boarding: Math.round(712_000 * 0.71),
    daycare: Math.round(318_000 * 0.66),
    grooming: Math.round(142_500 * 0.74),
    other: Math.round(76_000 * 0.62),
  },
  occupancy_pct: 64,
  occupancy_pct_ly: 67, // dropped YoY
  occupancy_change: -2.3,
  occupancy_detail: { boarding_pct: 64, daycare_avg: 41, peak_day: "Fri Apr 18", peak_pct: 81 },
  daycare_avg: 41,
  leads_total: Math.round(GC.leads_total * 0.69),
  leads_total_ly: Math.round(GC.leads_total * 0.69 * 0.88),
  leads_converted: Math.round(GC.leads_converted * 0.64),
  leads_converted_ly: Math.round(GC.leads_converted * 0.64 * 0.92),
  leads_qualified: Math.round(GC.leads_qualified * 0.78),
  conversion_rate: 51.8,
  conversion_rate_ly: 53.2, // slight YoY drop
  dogs_today: 52,
  dogs_today_ly: 48,
  going_home_today: 14,
  status: "active",
  health_score: 80,
};

const TENANT = {
  id: "tenant-gcpr",
  name: "Garden City Pet Resorts LLC",
  owner_email: "glen@gardencitypetresorts.com",
  owner_name: "Glen Roberts",
  plan: "trial",
  trial_ends_at: "May 30, 2026",
  trial_days_left: 18,
  max_locations: 2,
  locations_used: 1,
  gingr_subdomain: "gardencitypets",
  gingr_validated: true,
  created_at: "Feb 12, 2026",
};

// Hypothetical other tenants for the super-admin tenant list
const OTHER_TENANTS = [
  { id: "t-paws-1", name: "Pawsitively NYC", owner: "alex@pawsnyc.com", plan: "free", locations: 1, max: 1, status: "active" },
  { id: "t-bay-2", name: "Bay Area Resorts Group", owner: "sara@baypets.com", plan: "trial", locations: 2, max: 3, status: "active" },
  { id: "t-hou-3", name: "Houston Hounds Co", owner: "mike@houstonh.com", plan: "free", locations: 1, max: 1, status: "active" },
];

// Gingr partitions discovered under GC's API key (mock)
const GINGR_DISCOVERY_MOCK = [
  { gingr_id: 1, name: "Garden City", city: "Garden City, NY", already_added: true },
  { gingr_id: 2, name: "Syosset", city: "Syosset, NY", already_added: false },
];

// ── FORMATTERS ───────────────────────────────────────────────────────────
const fmtMoney = (n) => `$${(n / 1000).toFixed(0)}K`;
const fmtMoneyFull = (n) => `$${n.toLocaleString("en-US")}`;
const fmtPct = (n) => `${n}%`;

const ChangeChip = ({ value }) => {
  if (value == null || value === 0) return null;
  const positive = value > 0;
  const Icon = positive ? ArrowUp : ArrowDown;
  const color = positive ? "text-emerald-600 bg-emerald-50" : "text-red-600 bg-red-50";
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold ${color}`}>
      <Icon className="h-3 w-3" />
      {Math.abs(value)}%
    </span>
  );
};

// ── SCREEN 1: STACKED EXEC DASHBOARD ─────────────────────────────────────
function StackedExecView() {
  const [view, setView] = useState("rollup"); // rollup | comparison
  const [expanded, setExpanded] = useState(new Set([])); // location ids expanded
  const [range, setRange] = useState("ytd"); // shared across drill-down + comparison

  const rangeMul = useMemo(
    () => RANGE_OPTIONS.find(r => r.value === range)?.mul ?? 1.0,
    [range]
  );
  const rangeLabel = useMemo(
    () => RANGE_OPTIONS.find(r => r.value === range)?.label ?? "YTD",
    [range]
  );

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const rollup = useMemo(() => ({
    revenue_ytd: GC.revenue_ytd + SYOSSET.revenue_ytd,
    revenue_mtd: GC.revenue_mtd + SYOSSET.revenue_mtd,
    occupancy_pct: Math.round((GC.occupancy_pct + SYOSSET.occupancy_pct) / 2),
    daycare_avg: GC.daycare_avg + SYOSSET.daycare_avg,
    leads_total: GC.leads_total + SYOSSET.leads_total,
    leads_converted: GC.leads_converted + SYOSSET.leads_converted,
    dogs_today: GC.dogs_today + SYOSSET.dogs_today,
    conversion_rate: ((GC.leads_converted + SYOSSET.leads_converted) /
                      (GC.leads_total + SYOSSET.leads_total) * 100).toFixed(1),
  }), []);

  // For side-by-side comparison: each metric needs current + last_year for YoY
  const metrics = [
    { key: "revenue_ytd", lyKey: "revenue_ytd_ly", label: "Revenue YTD", fmt: fmtMoneyFull },
    { key: "occupancy_pct", lyKey: "occupancy_pct_ly", label: "Occupancy", fmt: (n) => `${n}%`, isPct: true },
    { key: "leads_total", lyKey: "leads_total_ly", label: "Leads YTD", fmt: (n) => n.toLocaleString() },
    { key: "leads_converted", lyKey: "leads_converted_ly", label: "Converted", fmt: (n) => n.toLocaleString() },
    { key: "conversion_rate", lyKey: "conversion_rate_ly", label: "Conv Rate", fmt: (n) => `${n}%`, isPct: true },
    { key: "dogs_today", lyKey: "dogs_today_ly", label: "Dogs On-Site", fmt: (n) => n.toLocaleString() },
  ];

  return (
    <div className="space-y-6" data-testid="stacked-exec-view">
      {/* Org header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-700 flex items-center justify-center shadow">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Org</div>
            <div className="text-xl font-bold text-gray-900">{TENANT.name}</div>
            <div className="text-xs text-gray-500">2 locations · Trial · {TENANT.trial_days_left} days left</div>
          </div>
        </div>
        <div className="inline-flex rounded-lg border bg-white p-1 shadow-sm">
          <button
            data-testid="view-toggle-rollup"
            onClick={() => setView("rollup")}
            className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${view === "rollup" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
          >
            <Layers className="h-3.5 w-3.5 inline mr-1" /> Roll-Up Totals
          </button>
          <button
            data-testid="view-toggle-comparison"
            onClick={() => setView("comparison")}
            className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${view === "comparison" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
          >
            <BedDouble className="h-3.5 w-3.5 inline mr-1" /> Side-by-Side
          </button>
        </div>
      </div>

      {view === "rollup" && (
        <div className="space-y-4">
          {/* Roll-up KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI title="Revenue YTD" value={fmtMoneyFull(rollup.revenue_ytd)} sub="All locations" icon={DollarSign} color="emerald" />
            <KPI title="Avg Occupancy" value={fmtPct(rollup.occupancy_pct)} sub="Boarding" icon={BedDouble} color="blue" />
            <KPI title="Leads (YTD)" value={rollup.leads_total} sub={`${rollup.leads_converted} converted`} icon={Users} color="indigo" />
            <KPI title="Dogs On-Site" value={rollup.dogs_today} sub="Today" icon={TrendingUp} color="amber" />
          </div>

          {/* Per-location table inside roll-up — expandable rows */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Breakdown by Location</span>
                <span className="text-xs font-normal text-gray-500">Click any row to expand</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-xs uppercase tracking-wider text-gray-500">
                      <th className="text-left py-2 font-semibold w-8"></th>
                      <th className="text-left py-2 font-semibold">Location</th>
                      <th className="text-right py-2 font-semibold">Revenue YTD</th>
                      <th className="text-right py-2 font-semibold">MTD</th>
                      <th className="text-right py-2 font-semibold">Occupancy</th>
                      <th className="text-right py-2 font-semibold">Leads</th>
                      <th className="text-right py-2 font-semibold">Conv %</th>
                      <th className="text-right py-2 font-semibold">Dogs Today</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[GC, SYOSSET].map(loc => {
                      const isExp = expanded.has(loc.id);
                      return (
                        <Fragment key={loc.id}>
                          <tr
                            className="border-b hover:bg-indigo-50 cursor-pointer transition-colors"
                            onClick={() => toggleExpand(loc.id)}
                            data-testid={`row-${loc.id}`}
                          >
                            <td className="py-3">
                              {isExp ? <ChevronDown className="h-4 w-4 text-indigo-600" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                            </td>
                            <td className="py-3">
                              <div className="flex items-center gap-2">
                                <MapPin className="h-3.5 w-3.5 text-gray-400" />
                                <span className="font-medium text-gray-900">{loc.name}</span>
                              </div>
                            </td>
                            <td className="text-right py-3 font-semibold">{fmtMoneyFull(loc.revenue_ytd)}</td>
                            <td className="text-right py-3">
                              <span className="inline-flex items-center gap-2">
                                {fmtMoney(loc.revenue_mtd)}
                                <ChangeChip value={loc.revenue_change} />
                              </span>
                            </td>
                            <td className="text-right py-3">
                              <span className="inline-flex items-center gap-2">
                                {fmtPct(loc.occupancy_pct)}
                                <ChangeChip value={loc.occupancy_change} />
                              </span>
                            </td>
                            <td className="text-right py-3">{loc.leads_total}</td>
                            <td className="text-right py-3">{loc.conversion_rate}%</td>
                            <td className="text-right py-3">{loc.dogs_today}</td>
                          </tr>
                          {isExp && (
                            <tr className="bg-slate-50 border-b">
                              <td colSpan={8} className="px-4 py-4">
                                <DrillDownDetail loc={loc} range={range} rangeLabel={rangeLabel} rangeMul={rangeMul} setRange={setRange} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                    <tr className="bg-indigo-50 font-bold">
                      <td className="py-3"></td>
                      <td className="py-3 text-indigo-900">Total</td>
                      <td className="text-right py-3 text-indigo-900">{fmtMoneyFull(rollup.revenue_ytd)}</td>
                      <td className="text-right py-3 text-indigo-900">{fmtMoney(rollup.revenue_mtd)}</td>
                      <td className="text-right py-3 text-indigo-900">{fmtPct(rollup.occupancy_pct)} <span className="text-[10px] font-normal text-gray-500">avg</span></td>
                      <td className="text-right py-3 text-indigo-900">{rollup.leads_total}</td>
                      <td className="text-right py-3 text-indigo-900">{rollup.conversion_rate}%</td>
                      <td className="text-right py-3 text-indigo-900">{rollup.dogs_today}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {view === "comparison" && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="text-base">Side-by-Side Performance · {rangeLabel}</CardTitle>
                <p className="text-xs text-gray-500">Bars normalized to leader. Vertical tick = last year same period. Δ = YoY%.</p>
              </div>
              <Select value={range} onValueChange={setRange}>
                <SelectTrigger className="w-44 h-8 text-xs" data-testid="range-select-comparison">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <ComparisonTable metrics={metrics} locations={[GC, SYOSSET]} rangeMul={rangeMul} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Drill-down Detail (revenue mix + occupancy detail per location) ─────
function DrillDownDetail({ loc, range, rangeLabel, rangeMul, setRange }) {
  // Apply range multiplier to revenue mix; occupancy stays as the snapshot %.
  const scaledMix = {
    boarding: Math.round(loc.revenue_mix.boarding * rangeMul),
    daycare: Math.round(loc.revenue_mix.daycare * rangeMul),
    grooming: Math.round(loc.revenue_mix.grooming * rangeMul),
    other: Math.round(loc.revenue_mix.other * rangeMul),
  };
  const mixTotal = scaledMix.boarding + scaledMix.daycare + scaledMix.grooming + scaledMix.other;
  const mixPct = (n) => mixTotal > 0 ? ((n / mixTotal) * 100).toFixed(1) : "0";
  const mixRows = [
    { label: "Boarding", value: scaledMix.boarding, color: "bg-indigo-500" },
    { label: "Daycare", value: scaledMix.daycare, color: "bg-cyan-500" },
    { label: "Grooming", value: scaledMix.grooming, color: "bg-amber-500" },
    { label: "Other", value: scaledMix.other, color: "bg-slate-400" },
  ];

  return (
    <div className="space-y-3">
      {/* Range selector inside drill-down */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5" /> {loc.name} · Detail
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Range:</span>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-44 h-8 text-xs bg-white" data-testid={`range-select-${loc.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Mix */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600" /> Revenue Mix · {rangeLabel}
            </h4>
            <span className="text-xs text-gray-500">Total {fmtMoneyFull(mixTotal)}</span>
          </div>
          <div className="space-y-2">
            {mixRows.map(row => (
              <div key={row.label}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-gray-700">{row.label}</span>
                  <span className="font-semibold text-gray-900">{fmtMoneyFull(row.value)} <span className="text-gray-500 font-normal">({mixPct(row.value)}%)</span></span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${row.color} rounded-full`} style={{ width: `${mixPct(row.value)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Occupancy Detail */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <BedDouble className="h-4 w-4 text-blue-600" /> Occupancy Detail · {rangeLabel}
            </h4>
            <ChangeChip value={loc.occupancy_change} />
          </div>
          <div className="space-y-3">
            <OccLine label="Boarding" pct={loc.occupancy_detail.boarding_pct} color="bg-blue-500" />
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-medium text-gray-700">Daycare avg / day</span>
                <span className="font-semibold text-gray-900">{loc.occupancy_detail.daycare_avg} dogs</span>
              </div>
            </div>
            <div className="border-t pt-3 mt-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Peak day this month</span>
                <span className="font-semibold text-gray-900">{loc.occupancy_detail.peak_day} <span className="text-emerald-600">({loc.occupancy_detail.peak_pct}%)</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OccLine({ label, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="font-semibold text-gray-900">{pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Comparison Table — bar with YoY tick + per-location YoY Δ ───────────
function ComparisonTable({ metrics, locations, rangeMul = 1.0 }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b text-xs uppercase tracking-wider text-gray-500">
            <th className="text-left py-3 font-semibold">Metric</th>
            {locations.map(loc => (
              <th key={loc.id} className="text-left py-3 font-semibold pl-3" colSpan={2}>
                <div className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${loc.id === "gc" ? "bg-indigo-600" : "bg-cyan-500"}`} />
                  {loc.name}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metrics.map(m => {
            // For pct metrics keep raw; for $ / counts apply range multiplier.
            const scale = (v) => m.isPct ? v : v * rangeMul;
            const values = locations.map(loc => scale(loc[m.key]));
            const lyValues = locations.map(loc => scale(loc[m.lyKey]));
            const max = Math.max(...values, ...lyValues);
            return (
              <tr key={m.key} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-3 font-medium text-gray-700 pr-3 align-middle">{m.label}</td>
                {locations.map((loc, i) => {
                  const val = values[i];
                  const ly = lyValues[i];
                  const widthPct = max > 0 ? (val / max) * 100 : 0;
                  const lyTickPct = max > 0 ? (ly / max) * 100 : 0;
                  const yoyPct = ly !== 0 ? ((val - ly) / ly * 100) : 0;
                  const barColor = loc.id === "gc" ? "bg-indigo-500" : "bg-cyan-500";
                  return (
                    <Fragment key={loc.id}>
                      <td className="py-3 pr-2 align-middle min-w-[180px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-6 bg-slate-100 rounded relative overflow-hidden">
                            {/* current bar */}
                            <div className={`h-full ${barColor} rounded transition-all`} style={{ width: `${widthPct}%` }} />
                            {/* LY tick mark */}
                            <div
                              className="absolute top-0 bottom-0 w-0.5 bg-gray-700"
                              style={{ left: `${lyTickPct}%` }}
                              title={`Last year: ${m.fmt(ly)}`}
                            />
                          </div>
                          <span className="text-xs whitespace-nowrap min-w-[70px] text-right font-bold text-gray-900">
                            {m.fmt(val)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 pr-4 align-middle">
                        <div className="flex flex-col items-end leading-tight">
                          <ChangeChip value={parseFloat(yoyPct.toFixed(1))} />
                          <span className="text-[10px] text-gray-400 mt-0.5">vs {m.fmt(ly)}</span>
                        </div>
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3 text-xs text-gray-500 flex items-center gap-4 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-indigo-600" /> Garden City</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cyan-500" /> Syosset</span>
        <span className="text-gray-400">·</span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-0.5 h-3 bg-gray-700" />
          Vertical tick = last year same period
        </span>
        <span className="text-gray-400">·</span>
        <span>Δ = year-over-year change</span>
      </div>
    </div>
  );
}

function KPI({ title, value, sub, icon: Icon, color }) {
  const colorMap = {
    emerald: "from-emerald-600 to-emerald-700",
    blue: "from-blue-600 to-blue-700",
    indigo: "from-indigo-600 to-indigo-700",
    amber: "from-amber-500 to-amber-600",
  };
  return (
    <Card className="overflow-hidden border-0 shadow-md">
      <div className={`h-1.5 bg-gradient-to-r ${colorMap[color]}`} />
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</span>
          {Icon && <Icon className="h-4 w-4 text-gray-400" />}
        </div>
        <div className="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight">{value}</div>
        {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function LocationCard({ loc }) {
  return null;
}



// ── SCREEN 2: TENANT ADMIN — ADD ANOTHER LOCATION FLOW ───────────────────
function TenantSettingsView() {
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [partitions, setPartitions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [provisioning, setProvisioning] = useState(false);
  const [addedLocations, setAddedLocations] = useState([GC]); // start with only GC

  const atCap = addedLocations.length >= TENANT.max_locations;

  const startDiscovery = () => {
    setDiscoveryOpen(true);
    setDiscovering(true);
    setPartitions([]);
    setSelected(new Set());
    // Fake discovery delay
    setTimeout(() => {
      const found = GINGR_DISCOVERY_MOCK.map(p => ({
        ...p,
        already_added: addedLocations.some(loc => loc.gingr_partition_id === p.gingr_id),
      }));
      setPartitions(found);
      setDiscovering(false);
    }, 1500);
  };

  const togglePartition = (gid) => {
    const next = new Set(selected);
    if (next.has(gid)) next.delete(gid);
    else next.add(gid);
    setSelected(next);
  };

  const provision = () => {
    setProvisioning(true);
    setTimeout(() => {
      const newOnes = partitions
        .filter(p => selected.has(p.gingr_id))
        .map(p => ({
          id: p.name.toLowerCase(),
          name: p.name,
          city: p.city,
          gingr_partition_id: p.gingr_id,
          revenue_ytd: 0,
          revenue_mtd: 0,
          occupancy_pct: 0,
          leads_total: 0,
          leads_converted: 0,
          conversion_rate: 0,
          dogs_today: 0,
          status: "syncing",
        }));
      setAddedLocations(prev => [...prev, ...newOnes]);
      setProvisioning(false);
      setDiscoveryOpen(false);
      toast.success(`${newOnes.length} location${newOnes.length > 1 ? "s" : ""} provisioned. Initial sync running…`);
    }, 1800);
  };

  return (
    <div className="space-y-6" data-testid="tenant-settings-view">
      {/* Tenant header chip */}
      <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-100">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Crown className="h-5 w-5 text-indigo-600" />
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Logged in as Tenant Admin</div>
              <div className="font-bold text-gray-900">{TENANT.owner_name} · {TENANT.name}</div>
            </div>
          </div>
          <Badge variant="outline" className="bg-white">
            <ShieldCheck className="h-3 w-3 mr-1 text-emerald-600" />
            Gingr key validated
          </Badge>
        </CardContent>
      </Card>

      {/* Locations list */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">Your Locations</CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              {addedLocations.length} of {TENANT.max_locations} used
            </p>
          </div>
          <Button
            data-testid="add-location-btn"
            onClick={startDiscovery}
            disabled={atCap}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {atCap ? <Lock className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Add Another Location
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {addedLocations.map(loc => (
              <div key={loc.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{loc.name}</div>
                    <div className="text-xs text-gray-500">
                      {loc.city} · Gingr partition #{loc.gingr_partition_id}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {loc.status === "syncing" ? (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Initial sync…
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Active
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>

          {atCap && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-900">
                <strong>You've reached your location cap ({TENANT.max_locations}).</strong>{" "}
                Need to add more? <a className="underline font-semibold" href="#">Contact your Emergent rep</a> to increase your plan.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Discovery dialog */}
      <Dialog open={discoveryOpen} onOpenChange={setDiscoveryOpen}>
        <DialogContent className="max-w-lg" data-testid="discovery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SettingsIcon className="h-4 w-4" /> Discover Gingr Locations
            </DialogTitle>
            <DialogDescription>
              We'll use your validated Gingr API key to find all locations under
              <strong> {TENANT.gingr_subdomain}.gingrapp.com</strong>.
            </DialogDescription>
          </DialogHeader>

          {discovering && (
            <div className="py-8 flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              <p className="text-sm text-gray-600">Scanning Gingr partitions…</p>
            </div>
          )}

          {!discovering && partitions.length > 0 && (
            <div className="space-y-2 py-2">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                Found {partitions.length} partition{partitions.length > 1 ? "s" : ""}
              </p>
              {partitions.map(p => (
                <label
                  key={p.gingr_id}
                  className={`flex items-center gap-3 border rounded-lg p-3 transition-colors ${
                    p.already_added
                      ? "bg-gray-50 opacity-60 cursor-not-allowed"
                      : "cursor-pointer hover:bg-indigo-50 hover:border-indigo-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.gingr_id)}
                    disabled={p.already_added}
                    onChange={() => togglePartition(p.gingr_id)}
                    className="h-4 w-4 accent-indigo-600"
                    data-testid={`partition-${p.gingr_id}`}
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.city} · Gingr partition #{p.gingr_id}</div>
                  </div>
                  {p.already_added && (
                    <Badge variant="outline" className="text-xs">Already added</Badge>
                  )}
                </label>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscoveryOpen(false)} disabled={provisioning}>
              Cancel
            </Button>
            <Button
              data-testid="provision-btn"
              onClick={provision}
              disabled={selected.size === 0 || provisioning || discovering}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {provisioning ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Provisioning…</>
              ) : (
                <>Onboard {selected.size} location{selected.size !== 1 ? "s" : ""}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── SCREEN 3: SUPER-ADMIN TENANT MANAGEMENT (UNIFIED with Location Mgmt) ─
function TenantManagementView() {
  const [editingCap, setEditingCap] = useState(null);
  const [capValue, setCapValue] = useState(2);
  const [expanded, setExpanded] = useState(new Set(["tenant-gcpr"])); // GC expanded by default
  const [tenants, setTenants] = useState(() => [
    {
      ...TENANT,
      owner: TENANT.owner_email,
      locations: [GC],
      max: TENANT.max_locations,
      status: "active",
    },
    ...OTHER_TENANTS.map(t => ({
      ...t,
      locations: Array.from({ length: t.locations }).map((_, i) => ({
        id: `${t.id}-loc${i + 1}`,
        name: i === 0 ? t.name.split(" ")[0] : `${t.name.split(" ")[0]} #${i + 1}`,
        city: "—",
        gingr_partition_id: i + 1,
        status: "active",
        health_score: 70 + Math.floor(Math.random() * 30),
        revenue_ytd: 200_000 + Math.floor(Math.random() * 800_000),
      })),
    })),
  ]);

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const startEdit = (t, e) => {
    e?.stopPropagation();
    setEditingCap(t.id);
    setCapValue(t.max);
  };

  const saveCap = (id, e) => {
    e?.stopPropagation();
    setTenants(prev => prev.map(t => t.id === id ? { ...t, max: capValue } : t));
    setEditingCap(null);
    toast.success("Location cap updated");
  };

  const planBadge = (plan) => {
    const map = {
      free: { c: "bg-slate-100 text-slate-700", l: "Free" },
      trial: { c: "bg-amber-100 text-amber-700", l: "Trial" },
      paid: { c: "bg-emerald-100 text-emerald-700", l: "Paid" },
    };
    const v = map[plan] || map.free;
    return <Badge className={v.c}>{v.l}</Badge>;
  };

  const healthChip = (score) => {
    const color = score >= 80 ? "bg-emerald-100 text-emerald-700" : score >= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
    return <Badge className={color}><Heart className="h-3 w-3 mr-1" /> {score}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="tenant-mgmt-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-600" /> Tenant & Location Management
          </h2>
          <p className="text-sm text-gray-500">
            Super-admin only · Unified 2-level view: Tenants → Locations. Click any tenant to drill in.
          </p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-tenant-btn">
          <Plus className="h-4 w-4 mr-2" /> Create New Tenant
        </Button>
      </div>

      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="p-4 flex items-start gap-3">
          <FlaskConical className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-700">
            <strong className="text-gray-900">Unification proposal:</strong> Today's standalone "Location Management" page becomes a drill-down inside Tenants.
            Single-tenant customers see no extra clicks (open URL → see their one tenant expanded by default).
            Multi-location customers benefit from the parent grouping.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-xs uppercase tracking-wider text-gray-500">
                  <th className="text-left px-4 py-3 font-semibold w-8"></th>
                  <th className="text-left px-4 py-3 font-semibold">Tenant</th>
                  <th className="text-left px-4 py-3 font-semibold">Owner</th>
                  <th className="text-center px-4 py-3 font-semibold">Plan</th>
                  <th className="text-center px-4 py-3 font-semibold">Locations</th>
                  <th className="text-center px-4 py-3 font-semibold">Cap</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => {
                  const isExp = expanded.has(t.id);
                  const usedCount = t.locations.length;
                  return (
                    <Fragment key={t.id}>
                      <tr
                        className="border-b hover:bg-indigo-50 cursor-pointer transition-colors"
                        onClick={() => toggleExpand(t.id)}
                        data-testid={`tenant-row-${t.id}`}
                      >
                        <td className="px-4 py-3">
                          {isExp ? <ChevronDown className="h-4 w-4 text-indigo-600" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{t.name}</div>
                          <div className="text-xs text-gray-500">{t.id}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{t.owner}</td>
                        <td className="px-4 py-3 text-center">{planBadge(t.plan)}</td>
                        <td className="px-4 py-3 text-center font-semibold">
                          <span className={usedCount >= t.max ? "text-amber-600" : "text-gray-700"}>
                            {usedCount} / {t.max}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {editingCap === t.id ? (
                            <div className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <Input
                                type="number"
                                min={1}
                                max={50}
                                value={capValue}
                                onChange={(e) => setCapValue(parseInt(e.target.value) || 1)}
                                className="w-16 h-7 text-center"
                                data-testid={`cap-input-${t.id}`}
                              />
                              <Button
                                size="sm"
                                onClick={(e) => saveCap(t.id, e)}
                                className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                                data-testid={`save-cap-${t.id}`}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); setEditingCap(null); }}
                                className="h-7 px-2"
                              >
                                ✕
                              </Button>
                            </div>
                          ) : (
                            <span className="font-semibold text-gray-700">{t.max}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {editingCap !== t.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => startEdit(t, e)}
                              className="h-7 text-xs"
                              data-testid={`edit-cap-${t.id}`}
                            >
                              Edit Cap
                            </Button>
                          )}
                        </td>
                      </tr>
                      {isExp && (
                        <tr className="bg-slate-50 border-b">
                          <td colSpan={7} className="px-6 py-4">
                            <div className="space-y-2">
                              <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-2">
                                <MapPin className="h-3.5 w-3.5" /> Locations under this tenant ({t.locations.length})
                              </div>
                              {t.locations.map(loc => (
                                <div
                                  key={loc.id}
                                  className="bg-white border rounded-lg p-3 flex items-center justify-between flex-wrap gap-3"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                                      <MapPin className="h-4 w-4 text-indigo-600" />
                                    </div>
                                    <div>
                                      <div className="font-semibold text-gray-900">{loc.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {loc.city || "—"} · Gingr partition #{loc.gingr_partition_id}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {loc.health_score != null && healthChip(loc.health_score)}
                                    {loc.revenue_ytd != null && (
                                      <Badge variant="outline" className="bg-white">
                                        <DollarSign className="h-3 w-3 mr-0.5" />
                                        {fmtMoneyFull(loc.revenue_ytd)} YTD
                                      </Badge>
                                    )}
                                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                      <CheckCircle2 className="h-3 w-3 mr-1" /> Active
                                    </Badge>
                                    <Button size="sm" variant="outline" className="h-7 text-xs">
                                      <Eye className="h-3.5 w-3.5 mr-1" /> Open
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              {t.locations.length < t.max && (
                                <div className="text-xs text-gray-500 mt-2 px-1">
                                  Customer can self-add up to {t.max - t.locations.length} more location{t.max - t.locations.length > 1 ? "s" : ""} via Settings → Locations.
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-blue-600" /> What this enables
          </h3>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>You manually create the <strong>Tenant</strong> + first location for each customer (security gate).</li>
            <li>Set their <strong>max_locations cap</strong> based on contract (e.g. 2 for Glen, 4 for the new prospect).</li>
            <li>Tenant admin self-adds remaining locations under their own Gingr key — no involvement from you.</li>
            <li>If they hit the cap, they contact you to bump it up (clean upsell hook for paid tiers / Stripe later).</li>
            <li>Old "Location Management" page folds into the tenant drill-down — one place to look, not two.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// ── PROTOTYPE WRAPPER ────────────────────────────────────────────────────
export default function TenantPrototype() {
  const cachedUser = JSON.parse(localStorage.getItem("cachedUser") || "{}");
  const isSuperAdmin = cachedUser.role === "super_admin";

  if (!isSuperAdmin) {
    return (
      <div className="p-8 max-w-md mx-auto mt-12">
        <Card>
          <CardContent className="p-6 text-center">
            <Lock className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-gray-900">Restricted</h2>
            <p className="text-sm text-gray-500 mt-1">This prototype is for super-admin review only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto" data-testid="tenant-prototype-page">
      {/* Prototype banner */}
      <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-3">
        <FlaskConical className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-bold text-amber-900 text-sm">Prototype — Multi-Location Tenant View</div>
          <div className="text-xs text-amber-800">
            Read-only mock for review. Garden City uses real-shape numbers; Syosset is synthesized at ~0.7×.
            No DB writes. Approve to start the real multi-tenancy build.
          </div>
        </div>
      </div>

      <Tabs defaultValue="exec" className="space-y-4">
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="exec" data-testid="tab-exec">
            <Layers className="h-4 w-4 mr-2" /> 1. Stacked Exec Dashboard
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Crown className="h-4 w-4 mr-2" /> 2. Tenant Admin (Glen's view)
          </TabsTrigger>
          <TabsTrigger value="mgmt" data-testid="tab-mgmt">
            <ShieldCheck className="h-4 w-4 mr-2" /> 3. Super-Admin Tenant Mgmt
          </TabsTrigger>
        </TabsList>

        <TabsContent value="exec"><StackedExecView /></TabsContent>
        <TabsContent value="settings"><TenantSettingsView /></TabsContent>
        <TabsContent value="mgmt"><TenantManagementView /></TabsContent>
      </Tabs>

      <Toaster position="top-right" />
    </div>
  );
}
